import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getDepressionFollowUpSummary } from '../HedisMetricService';
import DepressionFollowUpSummary from '../../../Models/HedisMetrics/DepressionFollowUpSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const DepressionFollowUpSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [depressionFollowUpSummary, setDepressionFollowUpSummary] = useState<DepressionFollowUpSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        depressionFollowUpHealthPlans,
        setDepressionFollowUpHealthPlans,
        depressionFollowUpTeam,
        setDepressionFollowUpTeam,
        depressionFollowUpDateFilter,
        setDepressionFollowUpDateFilter,
        depressionFollowUpFromDate,
        setDepressionFollowUpFromDate,
        depressionFollowUpToDate,
        setDepressionFollowUpToDate,
        depressionFollowUpIncludePastOpenStatus,
        setDepressionFollowUpIncludePastOpenStatus,
        depressionFollowUpShowInActiveTeam,
        setDepressionFollowUpShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'Depression Follow Up',
                    'Met vs Not Met',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (depressionFollowUpDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(depressionFollowUpFromDate) && Boolean(depressionFollowUpToDate))) {
            const changeFilter = async () => {
                let start = depressionFollowUpFromDate !== null ? new Date(depressionFollowUpFromDate).toDateString() : null;
                let end = depressionFollowUpToDate !== null ? new Date(depressionFollowUpToDate).toDateString() : null;

                const data = await getDepressionFollowUpSummary(
                    depressionFollowUpHealthPlans,
                    depressionFollowUpTeam,
                    depressionFollowUpDateFilter,
                    start,
                    end,
                    depressionFollowUpIncludePastOpenStatus,
                    depressionFollowUpShowInActiveTeam
                );
                setDepressionFollowUpSummary(data);
                setIsPrepared(true);
            };
            if ((depressionFollowUpFromDate !== null || depressionFollowUpToDate !== null) && depressionFollowUpToDate < depressionFollowUpFromDate) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Depression Follow Up',
                        'Met vs Not Met',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        depressionFollowUpHealthPlans,
        depressionFollowUpTeam,
        depressionFollowUpDateFilter,
        depressionFollowUpFromDate,
        depressionFollowUpToDate,
        depressionFollowUpIncludePastOpenStatus,
        depressionFollowUpShowInActiveTeam,
    ]);

    useEffect(() => {
        if (depressionFollowUpSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: depressionFollowUpSummary.Documented,
                name: 'Met',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Met', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: depressionFollowUpSummary.NotDocumented,
                name: 'Not Met',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Met', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            if (depressionFollowUpSummary.Documented === 0 && depressionFollowUpSummary.NotDocumented === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Depression Follow Up',
                        'Met vs Not Met',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('Depression Follow Up', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depressionFollowUpSummary, depressionFollowUpHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(depressionFollowUpHealthPlans);
        setTeam(depressionFollowUpTeam);
        setFilter(depressionFollowUpDateFilter);
        setFDate(depressionFollowUpFromDate);
        setTDate(depressionFollowUpToDate);
        setIncludePastOpenStatus(depressionFollowUpIncludePastOpenStatus);
        setShowInActiveTeam(depressionFollowUpShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.DepressionFollowUp);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/DepressionFollowUp`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Depression Follow Up
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">For members aged 18-75 within the rolling 12 month time frame</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Met :
                                    </Typography>

                                    <Typography variant="caption">
                                        Had an encounter with a CPT code of G8431 within 30 days of the G8511 CPT code being entered, or if G8431 is the more
                                        recent CPT code.
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Met :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={depressionFollowUpTeam}
                        setSelectedTeam={setDepressionFollowUpTeam}
                        dateFilter={depressionFollowUpDateFilter}
                        setDateFilter={setDepressionFollowUpDateFilter}
                        fromDate={depressionFollowUpFromDate}
                        setFromDate={setDepressionFollowUpFromDate}
                        toDate={depressionFollowUpToDate}
                        setToDate={setDepressionFollowUpToDate}
                        healthPlan={depressionFollowUpHealthPlans}
                        setHealthPlan={setDepressionFollowUpHealthPlans}
                        includePastOpenStatusMembers={depressionFollowUpIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setDepressionFollowUpIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.DepressionFollowUp}
                        showInActiveTeams={depressionFollowUpShowInActiveTeam}
                        setShowInActiveTeams={setDepressionFollowUpShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default DepressionFollowUpSummaries;
